import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  Users, Megaphone, MessageSquare, CheckCircle2, XCircle,
  Clock, TrendingUp, Activity
} from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, AreaChart, Area } from "recharts";

const API = '/api';

const StatCard = ({ icon: Icon, label, value, sub, color, delay }) => (
  <div
    data-testid={`stat-${label.toLowerCase().replace(/\s/g, "-")}`}
    className={`relative overflow-hidden bg-zinc-900 border border-white/5 rounded-lg p-5 flex flex-col gap-3 hover:border-white/10 transition-colors duration-200 animate-fade-in ${delay}`}
  >
    <div className="flex items-center justify-between">
      <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">{label}</span>
      <Icon className={`w-4 h-4 ${color}`} />
    </div>
    <div className="flex items-end gap-2">
      <span className="text-3xl font-heading font-bold tracking-tight text-white">{value}</span>
      {sub && <span className="text-xs text-zinc-500 mb-1">{sub}</span>}
    </div>
  </div>
);

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/stats/dashboard`);
      setStats(res.data);
    } catch (e) {
      // error silenced
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
    const interval = setInterval(fetchStats, 10000);
    return () => clearInterval(interval);
  }, [fetchStats]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const mockChartData = Array.from({ length: 12 }, (_, i) => ({
    hora: `${i * 2}:00`,
    enviados: Math.floor(Math.random() * 20),
    fallidos: Math.floor(Math.random() * 3),
  }));

  return (
    <div data-testid="dashboard-page" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-white uppercase">
            Panel de Control
          </h1>
          <p className="text-sm text-zinc-500 mt-1">Vision general del sistema de envio RCS</p>
        </div>
        <div className="flex items-center gap-2 bg-zinc-900 border border-white/5 rounded-lg px-3 py-2">
          <Activity className="w-3 h-3 text-emerald-500" />
          <span className="text-xs font-mono text-zinc-400">Sistema activo</span>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          icon={Users}
          label="Perfiles"
          value={stats?.profiles?.total || 0}
          sub={`${stats?.profiles?.connected || 0} conectados`}
          color="text-blue-400"
          delay="animate-fade-in-delay-1"
        />
        <StatCard
          icon={Megaphone}
          label="Campanas Activas"
          value={stats?.campaigns?.active || 0}
          sub={`${stats?.campaigns?.total || 0} total`}
          color="text-amber-400"
          delay="animate-fade-in-delay-2"
        />
        <StatCard
          icon={CheckCircle2}
          label="Mensajes Enviados"
          value={stats?.messages?.sent || 0}
          sub={`${stats?.messages?.success_rate || 0}% exito`}
          color="text-emerald-400"
          delay="animate-fade-in-delay-3"
        />
        <StatCard
          icon={XCircle}
          label="Fallidos"
          value={stats?.messages?.failed || 0}
          sub={`${stats?.messages?.pending || 0} pendientes`}
          color="text-red-400"
          delay="animate-fade-in-delay-4"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 bg-zinc-900 border border-white/5 rounded-lg p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading text-sm font-semibold uppercase tracking-wider text-zinc-400">
              Actividad de Envio
            </h3>
            <TrendingUp className="w-4 h-4 text-zinc-600" />
          </div>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={mockChartData}>
                <defs>
                  <linearGradient id="sentGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#3B82F6" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="hora" axisLine={false} tickLine={false} tick={{ fill: '#64748B', fontSize: 10 }} />
                <YAxis axisLine={false} tickLine={false} tick={{ fill: '#64748B', fontSize: 10 }} />
                <Tooltip
                  contentStyle={{ background: '#18181B', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', fontSize: '12px' }}
                  labelStyle={{ color: '#94A3B8' }}
                />
                <Area type="monotone" dataKey="enviados" stroke="#3B82F6" fill="url(#sentGrad)" strokeWidth={2} />
                <Area type="monotone" dataKey="fallidos" stroke="#EF4444" fill="transparent" strokeWidth={1.5} strokeDasharray="4 4" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-zinc-900 border border-white/5 rounded-lg p-5">
          <h3 className="font-heading text-sm font-semibold uppercase tracking-wider text-zinc-400 mb-4">
            Campanas Recientes
          </h3>
          <div className="space-y-3">
            {stats?.recent_campaigns?.length > 0 ? (
              stats.recent_campaigns.map((c) => (
                <div key={c.id} className="flex items-center justify-between py-2 border-b border-white/5 last:border-0">
                  <div>
                    <p className="text-sm text-zinc-200 font-medium">{c.name}</p>
                    <p className="text-[10px] font-mono text-zinc-600">
                      {c.stats?.sent || 0}/{c.stats?.total || 0} enviados
                    </p>
                  </div>
                  <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold ${
                    c.status === 'active' ? 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/20' :
                    c.status === 'paused' ? 'bg-yellow-500/15 text-yellow-400 border border-yellow-500/20' :
                    c.status === 'completed' ? 'bg-blue-500/15 text-blue-400 border border-blue-500/20' :
                    c.status === 'draft' ? 'bg-zinc-500/15 text-zinc-400 border border-zinc-500/20' :
                    'bg-red-500/15 text-red-400 border border-red-500/20'
                  }`}>
                    {c.status === 'active' ? 'Activo' : c.status === 'paused' ? 'Pausado' :
                     c.status === 'completed' ? 'Completado' : c.status === 'draft' ? 'Borrador' : 'Fallido'}
                  </span>
                </div>
              ))
            ) : (
              <p className="text-sm text-zinc-600 py-4 text-center">Sin campanas recientes</p>
            )}
          </div>
        </div>
      </div>

      <div className="bg-zinc-900 border border-white/5 rounded-lg p-5">
        <h3 className="font-heading text-sm font-semibold uppercase tracking-wider text-zinc-400 mb-4">
          Log de Actividad Reciente
        </h3>
        <div className="terminal-log max-h-48 overflow-y-auto">
          {stats?.recent_logs?.length > 0 ? (
            stats.recent_logs.map((log) => (
              <div key={log.id} className="flex gap-3 py-1">
                <span className="log-timestamp whitespace-nowrap">
                  [{log.timestamp ? new Date(log.timestamp).toLocaleTimeString('es-ES') : '--:--'}]
                </span>
                <span className={log.status === 'sent' ? 'log-sent' : 'log-failed'}>
                  {log.status === 'sent' ? 'ENVIADO' : 'FALLIDO'}
                </span>
                <span className="text-zinc-400">{log.phone}</span>
                <span className="text-zinc-600 truncate">{log.contact_name}</span>
              </div>
            ))
          ) : (
            <p className="text-zinc-600 text-center py-4">Sin actividad reciente</p>
          )}
        </div>
      </div>
    </div>
  );
}
